import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { FirebaseAuthService } from "./firebase-auth.service";
import { throwError, timer, from } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { AuthenticationService } from "./authentication-service";
import { AccountService } from "src/app/account/account.service";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { isLoginOrigin } from "./auth-origins";
import { Preferences } from "@capacitor/preferences";

const urlsToNotUse = [
  "auth/token/refresh",
  "auth/register",
  "auth/token",
  "auth/emailtoken/.",
  "auth/reset-password",
  "auth/reset-password/.",
  "error/report",
  "api/v1/comms/devices/.*",
];

// Public routes that don't require authentication
const publicRoutes = ["/welcome", "/slides", "/error"];

// Circuit breaker constants
const CIRCUIT_BREAKER_KEY = "auth_interceptor_failures";
const MAX_FAILURES = 3;
const RECOVERY_TIME_MS = 30000; // 30 seconds

export const AuthInterceptorService: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (!req.url.startsWith(`${environment.FLOCKFINDER_API_URL}/`)) return next(req);
  const ngFireAuth = inject(FirebaseAuthService);
  const authService = inject(AuthenticationService);
  const accountService = inject(AccountService);
  const router = inject(Router);

  // Add direct check for invitation endpoints before the main interceptor logic
  if (req.url.includes("/api/v1/invitations/") && (req.url.includes("/accept/") || req.url.endsWith("/"))) {
    return next(req);
  }

  if (isValidRequestForInterceptor(req.url)) {
    return ngFireAuth.idToken.pipe(
      take(1),
      switchMap(token => {
        if (token === null) {
          console.log("No token available");

          // Check if we're on a public route - if so, don't sign out
          const currentUrl = router.url;
          if (isPublicRoute(currentUrl)) {
            console.log("On public route, skipping SignOut");
            return throwError(() => "No token available");
          }

          // Check circuit breaker before attempting SignOut
          return from(checkCircuitBreaker()).pipe(
            switchMap(canProceed => {
              if (!canProceed) {
                console.log("Circuit breaker open, not calling SignOut");
                return throwError(() => "Circuit breaker open");
              }

              // Record the failure and attempt SignOut
              recordFailure();
              authService.SignOut();
              return throwError(() => "No token available");
            }),
          );
        }

        // Reset circuit breaker on successful auth
        resetCircuitBreaker();

        return addTokenAndHoldingId(req, token, accountService).pipe(
          switchMap(clonedReq => {
            return next(clonedReq).pipe(
              catchError(error => {
                if (
                  error instanceof HttpErrorResponse &&
                  error.status === 401 &&
                  error.error?.code === "email_unverified"
                ) {
                  return from(ngFireAuth.reloadCurrentUser()).pipe(
                    switchMap(user => (user ? from(user.getIdToken(true)) : throwError(() => error))),
                    switchMap(freshToken => next(clonedReq.clone({ setHeaders: { Authorization: freshToken } }))),
                  );
                }
                return throwError(() => error);
              }),
            );
          }),
        );
      }),
    );
  }

  return next(req);
};

// Helper functions moved outside
function isValidRequestForInterceptor(requestUrl: string): boolean {
  // First check if it's an Azure Blob Storage URL
  if (requestUrl.includes("blob.core.windows.net")) {
    return false;
  }

  // Then check the API paths
  const positionIndicator = "api/";
  const position = requestUrl.indexOf(positionIndicator);
  if (position > 0) {
    const destination = requestUrl.substr(position + positionIndicator.length);
    for (const address of urlsToNotUse) {
      if (new RegExp(address).test(destination)) {
        return false;
      }
    }
  }
  return true;
}

function isPublicRoute(url: string): boolean {
  return publicRoutes.some(route => url.startsWith(route));
}

async function checkCircuitBreaker(): Promise<boolean> {
  try {
    const result = await Preferences.get({ key: CIRCUIT_BREAKER_KEY });
    if (!result.value) {
      return true; // No failures recorded
    }

    const failures = JSON.parse(result.value);
    if (failures.count >= MAX_FAILURES) {
      const timeSinceLastFailure = Date.now() - failures.lastFailureTime;
      if (timeSinceLastFailure < RECOVERY_TIME_MS) {
        return false; // Circuit breaker is open
      } else {
        // Recovery time has passed, reset the circuit breaker
        await resetCircuitBreaker();
        return true;
      }
    }

    return true; // Under failure threshold
  } catch (error) {
    console.error("Error checking circuit breaker");
    return true; // Allow on error
  }
}

async function recordFailure(): Promise<void> {
  try {
    const result = await Preferences.get({ key: CIRCUIT_BREAKER_KEY });
    let failures = { count: 0, lastFailureTime: Date.now() };

    if (result.value) {
      failures = JSON.parse(result.value);
    }

    failures.count++;
    failures.lastFailureTime = Date.now();

    await Preferences.set({
      key: CIRCUIT_BREAKER_KEY,
      value: JSON.stringify(failures),
    });
  } catch (error) {
    console.error("Error recording failure");
  }
}

async function resetCircuitBreaker(): Promise<void> {
  try {
    await Preferences.remove({ key: CIRCUIT_BREAKER_KEY });
  } catch (error) {
    console.error("Error resetting circuit breaker");
  }
}

function addTokenAndHoldingId(req: HttpRequest<unknown>, token: string, accountService: AccountService) {
  return accountService.selectedUserHoldingGlobal.pipe(
    take(1),
    map(userHolding => {
      const headers: Record<string, string> = {
        Authorization: token,
      };

      // Extract holding ID from URL if present in holdings API path
      const holdingIdMatch = req.url.match(/\/api\/v(\d+)\/holdings\/(\d+)\//);
      const holdingId = holdingIdMatch ? holdingIdMatch[2] : userHolding?.holding?.toString();

      if (holdingId && !isLoginOrigin()) {
        headers["Selected-Holding-ID"] = holdingId;
      }

      return req.clone({ setHeaders: headers });
    }),
  );
}
